import Hogan from 'hogan.js';
import hoganHelpers from './algolia/helpers';
import { parseColorsFromAlgoliaNamedTags, responsiveImage, waitForAlgoliaClient } from './algolia/utils';

const buildQuickshopHtml = (card, additionalProductData) => {
  const variants = additionalProductData?.variants;
  const productId = additionalProductData?.id;
  const isBundleProduct = additionalProductData?.isBundleProduct;
  const productHandle = additionalProductData?.handle || '#';

  if (!variants || variants.length <= 0) return;

  let allVariantsOutOfStock = true;

  const options = variants
    .map(({ id, available, inventoryQuantity, optionLabel }) => {
      // Change allVariantsOutOfStock to false if a variant is available
      if (allVariantsOutOfStock && available) {
        allVariantsOutOfStock = false;
      }

      // Prepare Algolia tracking add to cart data
      const optionTrackingData = {
        index: card.dataset.algoliaIndex,
        eventName: 'Quickshop add to cart',
        queryID: card.dataset.algoliaQueryid,
        objectIDs: [String(id)],
      };

      if (window.algolia.customer) {
        optionTrackingData.authenticatedUserToken = `${window.algolia.customer}`;
      }

      return `
        <div class="options__option">
          <input
            id="${productId}-${id}"
            class="options__input visually-hidden"
            type="button"
            value="${id}"
            ${available ? '' : 'disabled'}
            data-algolia-tracking='${JSON.stringify(optionTrackingData)}'
            data-inventory-quantity="${inventoryQuantity}"
            data-quickshop-option
          />
          <label
            class="options__label${available ? '' : ' options__label--disabled'}"
            for="${productId}-${id}"
            role="button"
          >
            ${optionLabel}
          </label>
        </div>
      `;
    })
    .join('');

  return `
    <quick-shop class="quickshop">
      <div class="quickshop__popup">
        <div class="visibility-hidden" data-quickshop-options>
          ${
            allVariantsOutOfStock
              ? `
              <div class="quickshop__out-of-stock">
                Out of stock
              </div>
            `
              : ''
          }
          ${
            !isBundleProduct
              ? `
              <div class="quickshop__options options is-flex is-justified-start is-aligned-center">
                ${options}
              </div>
            `
              : ''
          }
        </div>
        ${
          !isBundleProduct
            ? `
          <button class="button button--secondary button--full-width quickshop__add-btn" data-quickshop-options-toggle>
            Quick Add
          </button>
          `
            : `
            <a href="/products/${productHandle}" class="button button--secondary button--full-width">
              Select your bundle
            </a>
          `
        }
      </div>
      <product-form class="product-form">
        <form
          method="post"
          action="/cart/add"
          id="product-card-form-${productId}"
          accept-charset="UTF-8"
          class="form"
          enctype="multipart/form-data"
          novalidate="novalidate"
          data-type="add-to-cart-form"
        >
          <input type="hidden" name="id" value="${variants[0]?.id}" />
          <input type="hidden" name="inventory_quantity" value="${variants[0]?.inventoryQuantity}" />

          <button
            type="submit"
            name="add"
            class="visually-hidden"
            data-add-to-cart-behavior="${window.algolia.addToCartBehavior}"
          ></button>
        </form>
      </product-form>
    </quick-shop>
  `;
};

const productCardDomChange = (card, additionalProductData) => {
  const cardHandle = card.dataset.handle;
  const { id, handle, title, vendor, isBundleProduct, imgUrl, imgAlternateUrl, badges, price, compareAtPrice, variants, variation_title } = additionalProductData;

  const imgSecondaryElem = card.querySelector('[data-product-card-image-secondary]');

  imgSecondaryElem.innerHTML = responsiveImage(title, imgAlternateUrl);

  // Quickshop
  const quickshopElem = card.querySelector('[data-product-card-quickshop]');
  quickshopElem.innerHTML = buildQuickshopHtml(card, additionalProductData);

  // No need to continue if we're hovering on the initial card
  if (cardHandle === handle) return;

  // Change card handle
  card.dataset.handle = handle;

  // Change primary image
  const imgPrimaryElem = card.querySelector('[data-product-card-image-primary]');
  imgPrimaryElem.innerHTML = responsiveImage(title, imgUrl);

  // Change badges
  const badgesElem = card.querySelector('[data-product-card-badges]');
  badgesElem.innerHTML = unescapeHTML(badges);

  // Change vendor
  const vendorElem = card.querySelector('[data-product-card-vendor]');
  if (variation_title && variation_title !== '') {
    vendorElem.innerHTML = `${vendor} • <span class="weight-regular">${variation_title}</span>`;
  } else {
    vendorElem.innerHTML = vendor;
  }

  // Change title
  const titleElem = card.querySelector('[data-product-card-title]');
  titleElem.innerHTML = title;

  // Change price
  const priceElem = card.querySelector('[data-product-card-price]');
  priceElem.innerHTML = formatPriceWithComparison(price, compareAtPrice);

  // Change product page urls
  card.querySelectorAll('[data-product-card-link]').forEach((anchor) => {
    anchor.href = `/products/${handle}`;
  });

  // Cange Wishlist Trigger Attributes
  const wishlistElem = card.querySelector('[data-wishlist-action]');

  if (!wishlistElem || !window._swat) return;

  wishlistElem.dataset.productId = id;
  wishlistElem.dataset.variantId = variants[0]?.id;
  wishlistElem.dataset.productDu = `${window.location.origin}/products/${handle}`;
  wishlistElem.dataset.productIu = imgUrl ? imgUrl.replace(/\/(.*)\.(\w{2,4})/g, '/$1_620x620.$2') : '';
  wishlistElem.dataset.productPr = price;
  wishlistElem.dataset.productDt = title;
};

const handleCardHover = async (event) => {
  const elem = event.currentTarget;
  const card = elem.closest('[data-product-card]');
  const productHandle = card?.dataset?.handle;
  const loadingClass = 'product-card--is-loading';

  if (!productHandle) return;

  try {
    card.classList.add(loadingClass);
    const additionalProductData = await fetchAdditionalProductData(productHandle);

    if (!additionalProductData) {
      throw new Error('No additionalProductData returned');
    }

    productCardDomChange(card, additionalProductData, false);
  } catch (error) {
    console.error('Error getting additional product data:', error);
  } finally {
    card.classList.remove(loadingClass);
  }
};

const handleSwatchClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();

  const elem = event.currentTarget;
  const card = elem.closest('[data-product-card]');
  const swatchProductHandle = elem.dataset?.productCardColorId;
  const loadingClass = 'product-card--is-loading';

  if (!swatchProductHandle) return;

  try {
    card.classList.add(loadingClass);
    const additionalProductData = await fetchAdditionalProductData(swatchProductHandle);

    if (!additionalProductData) {
      throw new Error('No additionalProductData returned');
    }

    const cardSwatches = elem.closest('[data-product-card]').querySelectorAll('[data-product-card-color-id]');
    const activeSwatchClass = 'product-card-colors__link--is-active';

    cardSwatches.forEach((cardSwatch) => cardSwatch.classList.remove(activeSwatchClass));
    elem.classList.add(activeSwatchClass);

    productCardDomChange(card, additionalProductData);
  } catch (error) {
    console.error('Error getting additional product data:', error);
  } finally {
    card.classList.remove(loadingClass);
  }
};

(async () => {
  if (!customElements.get('product-recommendations')) {
    const { default: Swiper } = await import('swiper');
    const { default: recommend } = await import('@algolia/recommend');

    // Checks if the algolia config has loaded before continuing
    try {
      await waitForAlgoliaClient();
    } catch (error) {
      console.error('Error while waiting for algolia:', error);
      return;
    }

    class ProductAlgoliaRecommendations extends HTMLElement {
      constructor() {
        super();

        this.algoliaConfig = window.algoliaShopify.config;

        this.recommendClient = recommend(this.algoliaConfig.app_id, this.algoliaConfig.search_api_key);

        this.swiperElement = this.querySelector('[data-swiper-related-products]');

        this.swiperOptions = {
          loop: false,
          autoplay: false,
          direction: 'horizontal',
          spaceBetween: 10,
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 16,
          breakpoints: {
            320: {
              slidesPerView: 2.2,
              spaceBetween: 10,
              slidesOffsetBefore: 16,
              slidesOffsetAfter: 16,
            },
            768: {
              slidesPerView: 3.2,
              spaceBetween: 10,
              slidesOffsetBefore: 16,
              slidesOffsetAfter: 16,
            },
            1024: {
              slidesPerView: 6,
              spaceBetween: 30,
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
            },
          },
        };

        // Check if we have extra options on the HTML
        if (this && this.dataset && this.dataset.options) {
          const jsonOptions = JSON.parse(this.dataset.options);

          if (jsonOptions) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...jsonOptions,
            };
          }
        }
      }

      connectedCallback() {
        // Initialize Intersection Observer
        const options = {
          root: null, // viewport as the bounding box
          rootMargin: '100px', // triggers 100px before the element comes into view
          threshold: 0.01, // trigger as soon as even 1% of the element is in view
        };
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.initRecommendations();
              observer.unobserve(entry.target); // Optionally unobserve after loading
            }
          });
        }, options);
        observer.observe(this);
      }

      async initRecommendations() {
        // re-init slider when Theme Editor is active
        if (Shopify.designMode && this.swiperElement) {
          // Update the swiper
          window.addEventListener('shopify:section:load', () => {
            this.swiperInstance.update();
          });
        }

        const template = Hogan.compile(document.querySelector('#product_list_card').innerHTML, { delimiters: '[[ ]]' });

        const colorsMap = this.dataset.colours
          ? this.dataset.colours.split(',').reduce((colors, colorStr) => {
              const parts = colorStr.split('|');
              if (parts[1]) {
                colors[parts[0]] = parts[1];
              }
              return colors;
            }, {})
          : {};

        const products = await this.recommendClient
          .getRecommendations([
            {
              indexName: `${this.algoliaConfig.index_prefix}products`,
              objectID: `${this.dataset.id}`,
              maxRecommendations: Number(this.dataset.maxProducts),
              model: this.dataset.type,
              queryParameters: {
                attributesToSnippet: [],
                attributesToHighlight: [],
                attributesToRetrieve: ['inventory_quantity', 'inventory_management', 'inventory_policy', 'handle', 'title', 'id', 'price_ratio', 'price', 'compare_at_price', 'vendor', 'product_image', 'image', 'variants_min_price', 'variants_max_price', 'variant_title', 'variants_inventory_count', 'named_tags', 'meta'],
                filters: 'inventory_quantity > 0',
              },
            },
          ])
          .then(({ results }) =>
            results[0].hits.map((product) => {
              const colors = parseColorsFromAlgoliaNamedTags(product, colorsMap);

              return {
                ...product,
                _distinct: true,
                can_order: product.inventory_management !== 'shopify' || product.inventory_policy === 'continue' || product.inventory_quantity > 0,
                queryID: product.__queryID,
                productPosition: product.__position,
                index: `${this.algoliaConfig.index_prefix}products`,
                colors,
              };
            })
          );

        if (products.length > 0) {
          this.querySelector('[data-related-products]').innerHTML = products
            .map(
              (product) => `
                <div class="swiper-slide">
                  ${template.render({
                    ...product,
                    helpers: hoganHelpers,
                  })}
                </div>
              `
            )
            .join('');

          this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);

          // Attach event listeners
          const allProductCards = this.querySelectorAll('[data-product-card]');

          allProductCards.forEach(async (card) => {
            // Retrieve additional data for quick shop
            const productHandle = card?.dataset?.handle;

            if (productHandle) {
              const additionalProductData = await fetchAdditionalProductData(productHandle);

              if (!additionalProductData) {
                throw new Error('No additionalProductData returned');
              }
              // Quickshop
              const quickshopElem = card.querySelector('[data-product-card-quickshop]');
              quickshopElem.innerHTML = buildQuickshopHtml(card, additionalProductData);
            }

            // Card hover events
            const hoverElem = card.querySelector('[data-product-card-inner]');

            hoverElem.removeEventListener('mouseenter', handleCardHover);
            hoverElem.addEventListener('mouseenter', handleCardHover, {
              once: true,
            });

            // Swatches click events
            const cardSwatches = card.querySelectorAll('[data-product-card-color-id]');

            cardSwatches.forEach((swatch) => {
              swatch.removeEventListener('click', handleSwatchClick);
              swatch.addEventListener('click', handleSwatchClick);
            });

            if (card.querySelector('[data-show-remaining-colours]')) {
              card.querySelector('[data-show-remaining-colours]').addEventListener('click', (e) => {
                e.target.remove();

                card.querySelectorAll('.product-card-colors__link--hidden').forEach((el) => {
                  el.classList.remove('product-card-colors__link--hidden');
                });
              });
            }
          });
        } else {
          this.remove();
        }
      }
    }

    window.ProductAlgoliaRecommendations = ProductAlgoliaRecommendations;

    customElements.define('product-algolia-recommendations', ProductAlgoliaRecommendations);
  }
})();
